// PrestoDualPlayer.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  LoadMode,
  PRIORITY,
  PRIORITY_BY_LOAD_MODE,
  buildSourceKey,
  buildDrmKey,
} from '../resource/playerConfig.js';
import { PlayerContext } from '../context/PlayerContext.js';
import createPlayer from '../resource/createPlayer.js';
import createVideoElement from '../resource/createVideoElement.js';
import createPlayerControl from '../resource/createPlayerControl.js';
import { getPlayerPool } from '../resource/VodPlayerPool.js';
import { getVideoElementPool } from '../resource/VideoElementPool.js';
import { EMPTY_OBJECT, NULL_FUNCTION } from '../resource/defaults.js';
import { player as playerDebug } from '../resource/debug.js';
import {
  hasValidSourceUrl,
  reportPlayerCallbackDeclarationMismatches,
} from '../resource/playerUtils.js';

const dualPlayerLog = playerDebug.extend('log:PrestoDualPlayer');
const dualPlayerError = playerDebug.extend('error:PrestoDualPlayer');

const Phase = Object.freeze({
  SPLASH: 'splash',
  SWITCHING: 'switching',
  MAIN: 'main',
  ERROR: 'error',
  MAIN_LOST_PRELOAD: 'main_lost_preload',
});

// Phase transitions:
//   (no splash) ────────────────────────────→ MAIN
//   SPLASH ── splash ended ─────────────────→ SWITCHING ── main ready ──→ MAIN
//   SPLASH ── main preempted ───────────────→ MAIN_LOST_PRELOAD
//   MAIN_LOST_PRELOAD ── splash ended ──────→ SWITCHING
//   MAIN ── error ──────────────────────────→ ERROR
//   SWITCHING / MAIN ── preempted ──────────→ (re-load in place, phase unchanged)

const Slot = Object.freeze({
  SPLASH: 'splash',
  MAIN: 'main',
});

const MAIN_FORWARDED_KEYS = [
  'onEnded',
  'onLoadStart',
  'onLoadedData',
  'onLoadedMetadata',
  'onSeeking',
  'onSeeked',
  'onProgress',
  'onVolumeChange',
  'onEnterPictureInPicture',
  'onTimeupdate',
];

const isSplashActivePhase = phaseValue =>
  phaseValue === Phase.SPLASH || phaseValue === Phase.MAIN_LOST_PRELOAD;

/** Dual-instance VOD player (splash -> main). */
const PrestoDualPlayer = ({
  source = null,
  splashVideo = null,
  drm = null,
  drmFeatureName = '',
  messageId = null,
  shouldUseSplash = false,
  loadMode = LoadMode.NONE,
  isMuted = true,
  style = EMPTY_OBJECT,
  children = null,
  onSetup = NULL_FUNCTION,
  onError = NULL_FUNCTION,
  onPlayerSwitch = NULL_FUNCTION,
  onLoadStart = NULL_FUNCTION,
  onLoadedData = NULL_FUNCTION,
  onLoadedMetadata = NULL_FUNCTION,
  onCanPlay = NULL_FUNCTION,
  onPlay = NULL_FUNCTION,
  onPlaying = NULL_FUNCTION,
  onPause = NULL_FUNCTION,
  onWaiting = NULL_FUNCTION,
  onEnded = NULL_FUNCTION,
  onProgressUpdate = NULL_FUNCTION,
  onTimeupdate = NULL_FUNCTION,
  onSeeking = NULL_FUNCTION,
  onSeeked = NULL_FUNCTION,
  onProgress = NULL_FUNCTION,
  onVolumeChange = NULL_FUNCTION,
  onEnterPictureInPicture = NULL_FUNCTION,
  onFullscreenChange = NULL_FUNCTION,
}) => {
  const playerContext = useContext(PlayerContext);
  const setPlayer = playerContext?.setPlayer;
  const setPlayerRef = useRef(setPlayer);
  setPlayerRef.current = setPlayer;

  // ── DOM ──
  const playerContainerRef = useRef(null);
  const splashContainerRef = useRef(null);
  const mainContainerRef = useRef(null);

  // ── Player Instances ──
  const splashPlayerRef = useRef(null);
  const mainPlayerRef = useRef(null);

  // ── Phase ──
  const phaseRef = useRef(null);
  const [phase, setPhase] = useState(null);
  const hasNotifiedPlayingRef = useRef(false);
  const hasFiredSetupRef = useRef(false);

  // ── Stable Props ──
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;

  const sourceRef = useRef({ source, splashVideo, drm, drmFeatureName });
  sourceRef.current = { source, splashVideo, drm, drmFeatureName };
  const callbacksRef = useRef({});
  callbacksRef.current = {
    onSetup,
    onError,
    onPlayerSwitch,
    onLoadStart,
    onLoadedData,
    onLoadedMetadata,
    onCanPlay,
    onPlay,
    onPlaying,
    onPause,
    onWaiting,
    onEnded,
    onProgressUpdate,
    onTimeupdate,
    onSeeking,
    onSeeked,
    onProgress,
    onVolumeChange,
    onEnterPictureInPicture,
    onFullscreenChange,
  };
  reportPlayerCallbackDeclarationMismatches({
    componentName: 'PrestoDualPlayer',
    callbacks: callbacksRef.current,
    propTypes: PrestoDualPlayer.propTypes,
    forwardedKeys: MAIN_FORWARDED_KEYS,
  });

  // ── Player Control ──
  const playerControlRef = useRef(null);
  if (!playerControlRef.current) {
    playerControlRef.current = createPlayerControl({
      getActivePlayer: () =>
        isSplashActivePhase(phaseRef.current) && splashPlayerRef.current
          ? splashPlayerRef.current
          : mainPlayerRef.current,
      getContainerElement: () => playerContainerRef.current,
      getSource: () => sourceRef.current,
      getCallbacks: () => callbacksRef.current,
      getIsSplashActive: () => isSplashActivePhase(phaseRef.current),
    });
  }

  // ── Derived State ──
  const hasValidMainSource = hasValidSourceUrl(source);
  const hasValidIntro = hasValidSourceUrl(splashVideo);
  const isSplashEnabled = Boolean(shouldUseSplash && hasValidIntro);
  const shouldLoadPlayer = loadMode !== LoadMode.NONE && hasValidMainSource;
  const mainSourceKey = buildSourceKey(source);
  const splashVideoKey = buildSourceKey(splashVideo);
  const drmKey = buildDrmKey(drm);
  // During splash, main stays PRELOAD regardless of outer loadMode.
  const effectiveMainLoadMode =
    isSplashEnabled && loadMode === LoadMode.ACTIVE
      ? LoadMode.PRELOAD
      : loadMode;
  const effectiveMainLoadModeRef = useRef(effectiveMainLoadMode);
  effectiveMainLoadModeRef.current = effectiveMainLoadMode;

  useEffect(() => {
    if (!messageId || !shouldLoadPlayer) return undefined;

    let pendingMainUnsubscribe = null;
    let splashSnapshotUnsubscribe = NULL_FUNCTION;

    dualPlayerLog(`(msgId ${messageId}) mount`, { isSplashEnabled });

    // ── Helpers ──

    const updatePhase = nextPhase => {
      const previousPhase = phaseRef.current;
      phaseRef.current = nextPhase;
      setPhase(nextPhase);
      dualPlayerLog(
        `(msgId ${messageId}) phase ${previousPhase} → ${nextPhase}`
      );
    };

    const playWithCurrentMute = player => {
      player?.setMuted(isMutedRef.current);
      return player?.play();
    };

    const logLoadFailure = label => error => {
      dualPlayerError(`(msgId ${messageId}) ${label} ERROR`, {
        error: error?.message || String(error),
      });
    };

    const getMainSnapshot = () => mainPlayerRef.current?.getSnapshot?.() || {};

    const notifyMainPlaying = (payload = { type: 'playing' }) => {
      if (hasNotifiedPlayingRef.current) return;
      hasNotifiedPlayingRef.current = true;
      callbacksRef.current.onPlay?.(payload);
      callbacksRef.current.onPlaying?.(payload);
    };

    // ── Splash → Main Switch ──

    // Synthesize onLoadedMetadata / onCanPlay / onProgressUpdate because the
    // main player loaded in preload phase — the surface never saw native events.
    const completeSwitchToMain = () => {
      if (phaseRef.current !== Phase.SWITCHING) return;
      const mainSnapshot = getMainSnapshot();
      dualPlayerLog(`(msgId ${messageId}) main ready → complete switch`, {
        mainState: mainPlayerRef.current?.state,
        hasReceived: mainSnapshot.hasReceived,
        isPlaying: mainSnapshot.isPlaying,
        isStalled: mainSnapshot.isStalled,
        currentTime: mainSnapshot.currentTime,
        duration: mainSnapshot.duration,
      });
      pendingMainUnsubscribe?.();
      pendingMainUnsubscribe = null;
      splashSnapshotUnsubscribe();
      splashSnapshotUnsubscribe = NULL_FUNCTION;
      updatePhase(Phase.MAIN);
      playerControlRef.current.syncListeners();
      splashPlayerRef.current?.release();
      splashPlayerRef.current = null;
      if (!mainSnapshot.isPlaying) {
        playWithCurrentMute(mainPlayerRef.current)?.catch(
          logLoadFailure('main play() after switch')
        );
      }
      callbacksRef.current.onLoadedMetadata?.();
      callbacksRef.current.onCanPlay?.(
        { type: 'canplay' },
        playerControlRef.current?.control
      );
      const mainVideo = mainPlayerRef.current?.getVideoElement?.();
      const mainDuration = mainVideo?.duration;
      if (mainDuration && Number.isFinite(mainDuration)) {
        callbacksRef.current.onProgressUpdate?.({
          currentTime: mainVideo?.currentTime || 0,
          duration: mainDuration,
        });
      }
      notifyMainPlaying();
      callbacksRef.current.onPlayerSwitch?.();
    };

    const handleSplashEnded = () => {
      const wasPreempted = phaseRef.current === Phase.MAIN_LOST_PRELOAD;
      if (phaseRef.current !== Phase.SPLASH && !wasPreempted) return;
      const main = mainPlayerRef.current;
      const mainSnapshot = main?.getSnapshot?.() || {};
      dualPlayerLog(`(msgId ${messageId}) splash ended → switching to main`, {
        wasPreempted,
        mainState: main?.state,
        hasReceived: mainSnapshot.hasReceived,
        isPlaying: mainSnapshot.isPlaying,
        isStalled: mainSnapshot.isStalled,
      });
      updatePhase(Phase.SWITCHING);
      playerControlRef.current.syncListeners();
      splashSnapshotUnsubscribe();
      splashSnapshotUnsubscribe = NULL_FUNCTION;
      splashPlayerRef.current?.release();
      splashPlayerRef.current = null;
      if (!main) {
        dualPlayerError(`(msgId ${messageId}) main missing while switching`);
        callbacksRef.current.onWaiting?.();
        return;
      }
      main.update({ loadMode: LoadMode.ACTIVE });
      if (wasPreempted) {
        main.load().catch(logLoadFailure('main re-load() (preempt recovery)'));
      }
      if (!mainSnapshot.hasReceived) callbacksRef.current.onWaiting?.();
      playWithCurrentMute(main)?.catch(logLoadFailure('main play()'));
      if (getMainSnapshot().hasReceived) {
        completeSwitchToMain();
        return;
      }
      pendingMainUnsubscribe = main.subscribe(snapshot => {
        if (snapshot.hasReceived) completeSwitchToMain();
      });
    };

    // ── Main Event Handlers ──

    const handleMainCanPlay = () => {
      if (phaseRef.current === Phase.SWITCHING) completeSwitchToMain();
      if (phaseRef.current === Phase.MAIN) {
        callbacksRef.current.onCanPlay?.(
          { type: 'canplay' },
          playerControlRef.current?.control
        );
      }
    };

    const handleMainWaiting = payload => {
      if (
        phaseRef.current !== Phase.SWITCHING &&
        phaseRef.current !== Phase.MAIN
      ) {
        return;
      }
      const mainSnapshot = getMainSnapshot();
      dualPlayerLog(`(msgId ${messageId}) main waiting`, {
        phase: phaseRef.current,
        mainState: mainPlayerRef.current?.state,
        hasReceived: mainSnapshot.hasReceived,
        isPlaying: mainSnapshot.isPlaying,
        isStalled: mainSnapshot.isStalled,
        currentTime: mainSnapshot.currentTime,
        duration: mainSnapshot.duration,
      });
      callbacksRef.current.onWaiting?.(
        payload,
        playerControlRef.current?.control
      );
    };

    const forwardCallback = key => payload => {
      if (phaseRef.current !== Phase.MAIN) return;
      callbacksRef.current[key]?.(payload, playerControlRef.current?.control);
    };

    const handleMainError = error => {
      dualPlayerError(`(msgId ${messageId}) main ERROR`, {
        error: error?.message || String(error),
      });
      updatePhase(Phase.ERROR);
      callbacksRef.current.onError?.(error);
    };

    const handleMainPreempted = () => {
      if (phaseRef.current === Phase.SPLASH) {
        updatePhase(Phase.MAIN_LOST_PRELOAD);
        return;
      }
      if (phaseRef.current === Phase.SWITCHING) {
        dualPlayerError(`(msgId ${messageId}) main preempted while switching`);
        callbacksRef.current.onWaiting?.();
        mainPlayerRef.current
          ?.load()
          .catch(logLoadFailure('main re-load() (switching preempt)'));
        return;
      }
      if (phaseRef.current === Phase.MAIN) {
        dualPlayerLog(
          `(msgId ${messageId}) main preempted during playback → re-loading`
        );
        callbacksRef.current.onWaiting?.();
        mainPlayerRef.current
          ?.load()
          .then(() => playWithCurrentMute(mainPlayerRef.current))
          .catch(logLoadFailure('main re-load() (main preempt)'));
      }
    };

    // ── Player Setup ──

    if (isSplashEnabled) {
      splashPlayerRef.current = createVideoElement({
        videoElementPool: getVideoElementPool(),
        source: {
          id: `dual-splash-${messageId}`,
          // splash must use nativeElement play
          // No `drmFeatureName` alongside it on purpose: this path is a plain
          // <video> playing a progressive mp4, so there is no adapter to read
          // it and none of the tuning it selects (ABR seed, rebuffering goal)
          // has anything to act on.
          src: {
            mp4:
              sourceRef.current.splashVideo?.dash ||
              sourceRef.current.splashVideo?.hls,
          },
        },
        priority: PRIORITY.ACTIVE,
        playerContainerRef: splashContainerRef,
        debugLabel: `dual-splash-${messageId}`,
        onCanPlay: () => {
          callbacksRef.current.onLoadedMetadata?.();
          callbacksRef.current.onCanPlay?.();
        },
        onEnded: handleSplashEnded,
      });
      splashPlayerRef.current
        .load()
        .then(() => playWithCurrentMute(splashPlayerRef.current))
        .catch(logLoadFailure('splash load()'));
      splashSnapshotUnsubscribe = splashPlayerRef.current.subscribe(
        playerControlRef.current.syncListeners
      );
      updatePhase(Phase.SPLASH);
    } else {
      updatePhase(Phase.MAIN);
    }

    mainPlayerRef.current = createPlayer({
      vodPlayerPool: getPlayerPool(),
      videoElementPool: getVideoElementPool(),
      source: {
        id: `dual-main-${messageId}`,
        src: sourceRef.current.source,
        drm: sourceRef.current.drm,
        drmFeatureName: sourceRef.current.drmFeatureName,
      },
      priority: PRIORITY_BY_LOAD_MODE[effectiveMainLoadModeRef.current],
      playerContainerRef: mainContainerRef,
      debugLabel: `dual-main-${messageId}`,
      onCanPlay: handleMainCanPlay,
      onWaiting: handleMainWaiting,
      onError: handleMainError,
      onPreempted: handleMainPreempted,
      onPlaying: payload => {
        if (phaseRef.current === Phase.SWITCHING) completeSwitchToMain();
        if (phaseRef.current !== Phase.MAIN) return;
        if (!hasNotifiedPlayingRef.current) {
          notifyMainPlaying(payload);
        } else {
          callbacksRef.current.onPlaying?.(payload);
        }
      },
      onPause: () => {
        if (phaseRef.current !== Phase.MAIN) return;
        hasNotifiedPlayingRef.current = false;
        callbacksRef.current.onPause?.();
      },
      onProgressUpdate: ({ currentTime, duration }) => {
        if (phaseRef.current !== Phase.MAIN) return;
        const liveDuration =
          mainPlayerRef.current?.getVideoElement?.()?.duration;
        const effectiveDuration =
          Number.isFinite(liveDuration) && liveDuration > 0
            ? liveDuration
            : duration;
        callbacksRef.current.onProgressUpdate?.({
          currentTime,
          duration: effectiveDuration,
        });
      },
      ...Object.fromEntries(
        MAIN_FORWARDED_KEYS.map(key => [key, forwardCallback(key)])
      ),
    });
    mainPlayerRef.current
      .load()
      .then(() => {
        if (effectiveMainLoadModeRef.current !== LoadMode.ACTIVE) return;
        playWithCurrentMute(mainPlayerRef.current);
      })
      .catch(logLoadFailure('main load()'));

    const mainSnapshotUnsubscribe = mainPlayerRef.current.subscribe(
      playerControlRef.current.syncListeners
    );

    const handleDocumentFullscreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ?? document.webkitFullscreenElement;
      const isFullscreen = fullscreenElement === playerContainerRef.current;
      callbacksRef.current.onFullscreenChange?.(isFullscreen);
    };
    if (typeof document !== 'undefined') {
      document.addEventListener(
        'fullscreenchange',
        handleDocumentFullscreenChange
      );
      document.addEventListener(
        'webkitfullscreenchange',
        handleDocumentFullscreenChange
      );
    }

    return () => {
      pendingMainUnsubscribe?.();
      pendingMainUnsubscribe = null;
      splashSnapshotUnsubscribe();
      mainSnapshotUnsubscribe();
      if (typeof document !== 'undefined') {
        document.removeEventListener(
          'fullscreenchange',
          handleDocumentFullscreenChange
        );
        document.removeEventListener(
          'webkitfullscreenchange',
          handleDocumentFullscreenChange
        );
      }
      playerControlRef.current.dispose();
      splashPlayerRef.current?.release();
      splashPlayerRef.current = null;
      mainPlayerRef.current?.release();
      mainPlayerRef.current = null;
    };
  }, [
    messageId,
    shouldLoadPlayer,
    isSplashEnabled,
    mainSourceKey,
    splashVideoKey,
    drmKey,
  ]);

  // Propagate loadMode changes to the main player (mirrors SinglePlayer).
  useEffect(() => {
    if (!mainPlayerRef.current) return;
    mainPlayerRef.current.update({
      loadMode: effectiveMainLoadMode,
      isBrowserTabVisible: true,
      autoplay: false,
    });
  }, [effectiveMainLoadMode]);

  useEffect(() => {
    if (!messageId || !shouldLoadPlayer) return undefined;
    if (loadMode !== LoadMode.ACTIVE) return undefined;
    if (hasFiredSetupRef.current) return undefined;
    hasFiredSetupRef.current = true;
    const { control } = playerControlRef.current;
    const currentSetPlayer = setPlayerRef.current;

    callbacksRef.current.onSetup(control);
    currentSetPlayer?.(control);
    return () => {
      hasFiredSetupRef.current = false;
      currentSetPlayer?.(null);
    };
  }, [messageId, shouldLoadPlayer, loadMode]);

  useEffect(() => {
    getPlayerPool().syncMutedState(isMuted);
  }, [isMuted]);

  const isMainPhase =
    phase === Phase.SWITCHING || phase === Phase.MAIN || phase === Phase.ERROR;
  const currentSlot = !isSplashEnabled || isMainPhase ? Slot.MAIN : Slot.SPLASH;

  return (
    <PlayerContainer ref={playerContainerRef} style={style}>
      {isSplashEnabled ? (
        <SlotContainer
          ref={splashContainerRef}
          data-slot={Slot.SPLASH}
          $isVisible={currentSlot === Slot.SPLASH}
        />
      ) : null}
      <SlotContainer
        ref={mainContainerRef}
        data-slot={Slot.MAIN}
        $isVisible={currentSlot === Slot.MAIN}
      />
      {children}
    </PlayerContainer>
  );
};

PrestoDualPlayer.propTypes = {
  source: PropTypes.object,
  splashVideo: PropTypes.object,
  drm: PropTypes.object,
  drmFeatureName: PropTypes.string,
  messageId: PropTypes.string,
  shouldUseSplash: PropTypes.bool,
  loadMode: PropTypes.oneOf(Object.values(LoadMode)),
  isMuted: PropTypes.bool,
  style: PropTypes.object,
  children: PropTypes.node,
  onSetup: PropTypes.func,
  onError: PropTypes.func,
  onPlayerSwitch: PropTypes.func,
  onLoadedMetadata: PropTypes.func,
  onEnded: PropTypes.func,
  onPause: PropTypes.func,
  onPlay: PropTypes.func,
  onProgressUpdate: PropTypes.func,
  onSeeking: PropTypes.func,
  onSeeked: PropTypes.func,
  onProgress: PropTypes.func,
  onVolumeChange: PropTypes.func,
  onEnterPictureInPicture: PropTypes.func,
  onCanPlay: PropTypes.func,
  onPlaying: PropTypes.func,
  onWaiting: PropTypes.func,
  onTimeupdate: PropTypes.func,
  onFullscreenChange: PropTypes.func,
  onLoadStart: PropTypes.func,
  onLoadedData: PropTypes.func,
};

const PlayerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const SlotContainer = styled.div`
  position: absolute;
  inset: 0;
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};

  & > div {
    width: 100%;
    height: 100%;
  }

  video {
    width: 100%;
    height: 100%;
  }
`;

export default PrestoDualPlayer;
